import { SOFTLINE_CONFIG_LIEFERSCHEIN } from './delivery-note.shared';
import {
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import {
  ApplicationStore,
  getModuleGroupByName,
  SOFTLINE_CONFIG_OFFLINE_FEATURE,
  SOFTLINE_FEATURE_APPLICATIONS,
} from '@softline/application';
import {
  provideSoftAppsModule,
  withLazySoftAppsModuleTranslation,
  withSoftAppsModuleInfo,
  withSoftAppsModuleRoutes,
  provideSoftAppsModuleConfig,
  provideSoftAppsModuleInfo,
} from '@softapps/core';
import {
  ConnectionHttpService,
  SOFTLINE_SERVICE_HTTP,
  SOFTLINE_STORE_ENTITY_SERVICE,
  SoftlineEntityService,
  Store,
  StoreModule,
} from '@softline/core';
import { SOFTLINE_FEATURE_TRANSLATION, TranslationStore } from '@softline/ui-core';
import { default as de } from '../i18n/de.json';
import { default as deTitles } from '../i18n/de.titles.json';
import { lieferscheinRoutes } from './lieferschein.routes';
import { provideLieferscheinGlobals } from './lieferschein.globals';
import { SOFTLINE_API_DELIVERY_NOTES } from './delivery-note.api';
import {
  SOFTLINE_FEATURE_DELIVERY_NOTE,
  SOFTLINE_FEATURE_RETURN_NOTE,
  SOFTLINE_PERMISSION_LIEFERSCHEIN,
} from './delivery-note.shared';
import { DeliveryNotesStore } from './stores';
import { DeliveryNoteConfig } from './types/delivery-note-config';
import { ReturnNoteStore } from './stores/return-note.store';

export const lieferscheinProviders: (Provider | EnvironmentProviders)[] = [
  importProvidersFrom(
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_DELIVERY_NOTE,
      feature: DeliveryNotesStore.feature,
      providers: [
        {
          provide: SOFTLINE_STORE_ENTITY_SERVICE,
          useFactory: (service: ConnectionHttpService) =>
            new SoftlineEntityService(service, SOFTLINE_API_DELIVERY_NOTES),
          deps: [SOFTLINE_SERVICE_HTTP],
        },
      ],
    }),
    StoreModule.forFeature({
      name: SOFTLINE_FEATURE_RETURN_NOTE,
      feature: ReturnNoteStore.feature,
      providers: [],
    })
  ),
  provideEnvironmentInitializer(() => {
    const store = inject(Store);
    store.commit(SOFTLINE_FEATURE_TRANSLATION, TranslationStore.mutations.add, {
      module: 'lieferschein',
      language: 'de',
      translations: de,
    });
    store.commit(SOFTLINE_FEATURE_APPLICATIONS, ApplicationStore.mutations.add, {
      name: '#DELIVERY_NOTE.TITLE',
      icon: 'fa-regular fa-truck-arrow-right',
      route: '/lieferscheine',
      permission: SOFTLINE_PERMISSION_LIEFERSCHEIN,
      group: getModuleGroupByName('Stammdaten'),
    });
  }),
  provideSoftAppsModuleInfo('#DELIVERY_NOTE.TITLE', {
    library: '@softapps/wws/lieferschein',
    description: 'Lieferscheine erfassen und abschließen.',
  }),
  provideSoftAppsModuleConfig('#DELIVERY_NOTE.TITLE', SOFTLINE_CONFIG_LIEFERSCHEIN),
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_DELIVERY_NOTE,
      fields: ['ids', 'entities', 'preparedChanges', 'viewedDate'],
    },
    multi: true,
  },
  {
    provide: SOFTLINE_CONFIG_OFFLINE_FEATURE,
    useValue: {
      featureName: SOFTLINE_FEATURE_RETURN_NOTE,
      fields: ['ids', 'entities', 'preparedChanges'],
    },
    multi: true,
  },
];

export function provideLieferscheinModule(config?: Partial<DeliveryNoteConfig>) {
  return [
    provideSoftAppsModule(
      {
        name: 'wws/lieferschein',
        title: '#DELIVERY_NOTE.TITLE',
        icon: 'fa-regular fa-truck-arrow-right',
        route: '/lieferscheine',
        permission: SOFTLINE_PERMISSION_LIEFERSCHEIN,
        group: getModuleGroupByName('Stammdaten'),
      },
      withSoftAppsModuleRoutes({ app: lieferscheinRoutes }),
      withLazySoftAppsModuleTranslation({
        eager: { de: deTitles },
        lazy: { de: () => import('../i18n/de.json') },
      }),
      withSoftAppsModuleInfo({
        library: '@softapps/wws/lieferschein',
        description: 'Lieferscheine erfassen und abschließen.',
      })
    ),
    ...provideLieferscheinGlobals(config),
  ];
}
